import React, { MouseEvent, useContext } from 'react';

import cn from 'classnames';
import { Trans } from 'react-i18next';

import { isAuthenticated } from 'actions/user';
import Popover from 'containers/Popover';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { preLoginFavoriteClick } from 'helpers/utils';
import { userSelector } from 'selectors/otherSelectors';
import { getProductByID } from 'selectors/tradeSelectors';
import { useAppSelector } from 'storeHooks';
import { StarIcon } from 'styles/assets/icons';
import LabelRibonMobile from 'styles/assets/icons/LabelRibonMobile';
import Box from 'UIKit/Box';

import styles from '../universal-search.module.scss';
import { FavoritesContext } from './helpers';
import { SearchResultProps } from './interface';

const SearchResult = (props: SearchResultProps) => {
  const { productId, symbol, contractType, changeIn24h, onRowClick } = props;

  const { favoritesIds, toggleFavorite } = useContext(FavoritesContext);
  const isFavorite = favoritesIds.includes(productId);
  const {
    ui_config: { tags },
  } = useAppSelector(state =>
    getProductByID(state, {
      filterProductId: productId,
    })
  );
  const showTags = tags !== undefined;
  const user = useAppSelector(state => userSelector(state));
  const isLoggedIn: boolean = isAuthenticated(user);

  const renderTag = tag => {
    switch (tag?.toLowerCase()) {
      case 'new':
        return <LabelRibonMobile />;
      default:
        return null;
    }
  };

  const onFavoriteMarked = () => {
    trackMixpanelEvent('Favorite Marked - Trade', {
      source: 'universal search',
      product_name: symbol,
      product_type: contractType,
    });
  };

  const onFavoriteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    toggleFavorite(productId, onFavoriteMarked);
  };

  const formatChangeIn24h = (value: number) => {
    return value > 0 ? `+${value.toFixed(2)}%` : `${value.toFixed(2)}%`;
  };

  return (
    (<Box
      as="div"
      data-testid={`${symbol}-row`}
      className={styles['coin-row']}
      onClick={onRowClick}
    >
      <div className={styles['coin-subhead-wrapper']}>
        {isLoggedIn ? (
          <button
            type="button"
            data-testid={`${symbol}-favoriteBtn`}
            onClick={onFavoriteClick}
            className={`${styles.favoriteBtn}`}
            data-palette="SearchResult">
            <StarIcon
              className={`${styles.starIcon} ${!isFavorite && styles.iconInActive}`}
            />
          </button>
        ) : (
          <Popover
            body={<Trans i18nKey="trading:addWatchlistToolTip" />}
            shouldEnableClickListener
          >
            {(thisProps: { setOpen: React.Dispatch<React.SetStateAction<boolean>> }) => (
              <button
                type="button"
                data-testid={`${symbol}-favoriteBtn`}
                onClick={e => preLoginFavoriteClick(e, thisProps.setOpen)}
                className={`${styles.favoriteBtn}`}
                data-palette="SearchResult">
                <StarIcon
                  className={`${styles.starIcon} ${!isFavorite && styles.iconInActive}`}
                />
              </button>
            )}
          </Popover>
        )}
        <div className={styles['coin-name']}>{symbol}</div>
        {showTags && (
          <div className="tag-mobile" data-palette="SearchResult">
            {tags.map(tag => (
              <div className={styles.tag} data-palette="SearchResult">{renderTag(tag)}</div>
            ))}
          </div>
        )}
      </div>
      <div
        className={cn(styles['coin-value'], {
          [styles.positive]: changeIn24h > 0,
          [styles.negative]: changeIn24h < 0,
        })}
      >
        {changeIn24h ? <span data-palette="SearchResult">{formatChangeIn24h(changeIn24h)}</span> : '-'}
      </div>
    </Box>)
  );
};

export default SearchResult;
